// import { DrawerModule } from 'primeng/drawer';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FileUploadModule, FileUpload } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ButtonModule } from 'primeng/button';
import { AtividadeAnexo } from '../models/atividade-anexo';
import { AtividadeAnexosService } from './atividade-anexos.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { TamanhoAnexo } from '@/core/constants/atividade.constants';
import { UserService } from '@/core/service/user.service';

@Component({
    selector: 'mf-atividade-anexo',
    templateUrl: './atividade-anexos.component.html',
    styleUrls: ['atividade-anexos.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        FileUploadModule,
        InputTextModule,
        ToastModule,
        TableModule,
        ConfirmDialogModule,
        ButtonModule
    ],
    providers: [MessageService, ConfirmationService]
})
export class AtividadeAnexosComponent implements OnInit {
  atividadeAnexos: AtividadeAnexo[] = [];
  @Input() idAtividade: number = 0;
  showForm = false;
  anexoAddForm!: FormGroup;
  uploadedFiles: File[] = [];
  @ViewChild('fileUpload') fileUpload!: FileUpload;
  arquivoSelecionado!: File; // Propriedade para armazenar o arquivo
  flagIns: boolean = false;
  flagDel: boolean = false;
  loading: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private atividadeAnexoService: AtividadeAnexosService,
    private messageService: MessageService,
    private activatedRoute: ActivatedRoute,
    private confirmationService: ConfirmationService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.getAnexos();
    this.flagIns = this.userService.hasPermission('WFM_ATIVIDADE_ANEXO_INS');
    this.flagDel = this.userService.hasPermission('WFM_ATIVIDADE_ANEXO_DEL');
    this.anexoAddForm = this.formBuilder.group({
      nome: ['', [Validators.required, Validators.maxLength(100)]],
      descricao: ['', [Validators.required, Validators.maxLength(250)]],
      observacao: ['', Validators.maxLength(500)],
    });
  }

  getAnexos() {
    this.atividadeAnexoService.getAtividadeAnexos(this.idAtividade).subscribe((atvAnexos) => {
      this.atividadeAnexos = atvAnexos;
    });
  }

  inserirAnexo() {
    this.showForm = !this.showForm;
  }

  onFileRemove(event: any) {
    this.uploadedFiles = this.uploadedFiles.filter((f) => f !== event.file);
  }

  onFileSelect(event: any) {
    for (const f of event.files) {
      if (f.size > TamanhoAnexo.MAX_SIZE) {
        this.messageService.add({
          severity: 'error',
          summary: 'O arquivo excedeu o tamanho máximo suportado.',
        });
        break;
      }
      this.uploadedFiles = [];
      this.uploadedFiles.push(f);
    }
  }

  saveAnexo(): void {
    const nome = this.anexoAddForm.get('nome')?.value;
    const descricao = this.anexoAddForm.get('descricao')?.value;
    const observacao = this.anexoAddForm.get('observacao')?.value;

    if (!this.uploadedFiles.length) {
      this.messageService.add({ 
        severity: 'error', 
        summary: 'Atenção', 
        detail: 'Por favor, selecione um arquivo.',
        life: 3000
      });
      return;
    }

    const arquivo = this.uploadedFiles[0];
    const extensao = arquivo.name.split('.').pop()?.toLowerCase();

    const extensoesPermitidas = ['jpg', 'jpeg', 'png', 'xls', 'xlsx', 'pdf'];
    if (!extensoesPermitidas.includes(extensao || '')) {
      this.messageService.add({
        severity: 'error',
        summary: 'Atenção',
        detail: 'Insira somente arquivos com extensões permitidas: .jpg, .jpeg, .png, .pdf, .xls ou .xlsx',
        life: 3000
      });
      return;
    }

    const formData = new FormData();
    formData.append('arquivo', arquivo, arquivo.name);
    formData.append('nome', nome);
    formData.append('descricao', descricao);
    formData.append('observacao', observacao);

    this.atividadeAnexoService.upload(formData, this.idAtividade).subscribe({
      next: () => {
        this.messageService.add({ 
          severity: 'success', 
          summary: 'Sucesso', 
          detail: 'Arquivo anexado com sucesso!',
          life: 3000
        });
        this.uploadedFiles = [];
        this.resetAnexoAddForm();
        this.anexoAddForm.reset();
        this.inserirAnexo();
        this.getAnexos();
      },
      error: (error) => {
        const errorMessage = error?.error?.message || error?.error?.detail || error?.error || error?.message || 'Erro ao anexar o arquivo';
        this.messageService.add({
          severity: 'error',
          summary: 'Erro ao anexar o arquivo',
          detail: errorMessage,
          life: 5000
        });
      },
    });
  }

  resetAnexoAddForm() {
    this.fileUpload.clear();
    this.anexoAddForm.reset();
    this.anexoAddForm.markAsPristine();
    this.anexoAddForm.markAsUntouched();
    this.uploadedFiles = [];
    return false;
  }

  deleteAnexo(idAtivadeAnexo: any, event: Event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Deseja excluir o anexo?',
      header: 'Confirmação de exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      acceptIcon: 'none',
      rejectIcon: 'none',

      accept: () => {
        this.atividadeAnexoService.deleteAnexo(idAtivadeAnexo).subscribe({
          next: (params) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Sucesso',
              detail: 'Anexo removido com sucesso.',
              life: 3000,
            });
            this.getAnexos();
          },
          error: (error) => {
            const errorMessage = error?.error?.message || error?.error?.detail || error?.error || error?.message || 'Falha ao remover anexo.';
            this.messageService.add({
              severity: 'error',
              summary: 'Erro',
              detail: errorMessage,
              life: 5000,
            });
            this.getAnexos();
          },
        });
      },
      reject: () => {
        this.messageService.add({ severity: 'info', summary: 'Cancelada', detail: 'Operação cancelada' });
      },
    });
  }

  download() {
    this.messageService.add({ severity: 'info', summary: 'Download', detail: 'Fazendo Download...' });
  }
}
